import { type TagJSONObject } from '@ikegps/core-api';
import { action, type IReactionDisposer, reaction, makeObservable } from 'mobx';
import { lineDrawNodes as buildLineDrawNodes } from '../../components/line.field.polyline';
import { MapPointDragger } from '../../components/map.point.drag';
import { addStyles } from '../../components/theme';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TagFetchState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { PointCache } from '../google/projection.cache';
import { CollectionMapPoint } from './point.collection';
import { TagMapPoint } from './point.tag';
import { type TagMapState, ZOOM_SWITCH_JOBS_LEVEL, ZOOM_SWITCH_LEVEL } from './tag.map.state';

export const CSS = {
	container: 'c-MapOverlay',
	tag: 'c-MapOverlay__tag',
	line: 'c-MapOverlayLayer__line',
	point: 'c-MapOverlayLayer__point',
};

addStyles({
	[CSS.container]: {
		width: '100%',
		height: '100%',
	},
	[CSS.tag]: {
		position: 'absolute',
	},
});

export interface OverlayLayers {
	lines: MithrilVNode;
	points: MithrilVNode;
}

export class CustomMapOverlay {
	name = 'MapOverlay';

	private el: Element;
	private state: TagMapState;
	private map: google.maps.Map;
	private centerReactionDisposer: IReactionDisposer;
	private overlay: google.maps.OverlayView;
	private origin: google.maps.LatLng;

	private readonly pointDragger = new MapPointDragger(() => this.overlay.getProjection());

	constructor(state: TagMapState, map: google.maps.Map) {
		makeObservable(this);

		this.overlay = new google.maps.OverlayView();
		this.state = state;
		this.setMap(map);
		this.updateMapBounds();

		this.overlay.onAdd = this.onAdd.bind(this);
		this.overlay.draw = this.onDraw.bind(this);
		this.overlay.onRemove = this.remove.bind(this);

		this.origin = new google.maps.LatLng(0, 0);
	}

	@action.bound private updateState() {
		this.state.setZoom(this.map.getZoom());
		this.state.setBounds(this.map.getBounds());
	}

	@action setMap(map: google.maps.Map) {
		this.map = map;

		if (map == null) {
			this.remove();

			if (this.centerReactionDisposer != null) {
				this.centerReactionDisposer();
				this.centerReactionDisposer = null;
			}
		}

		this.overlay.setMap(map);
	}

	onAdd() {
		this.el = this.overlay.getPanes().overlayMouseTarget;
		Services.Render.mount(this.el, this, this.name);
	}

	onDraw() {
		// Update state on map render tick to keep DOM in sync with map (events fire too late)
		this.updateState();

		const reset = this.verifyProjection();
		if (reset) {
			Services.Render.render(this.el, this.view());
		}
	}

	remove() {
		if (this.el) {
			Services.Render.mount(this.el, null, this.name);
			this.el = null;
		}
	}

	view(): MithrilVNode[] {
		const bounds = this.state.boundString.get();

		if (bounds == null || this.el == null || this.map == null) {
			return null;
		}
		this.verifyProjection();

		const base = this.baseLayers();
		const draw = this.lineDrawNodes;
		return draw.length ? [...base, ...draw] : base;
	}

	private baseLayers(): MithrilVNode[] {
		const visibleTags = this.state.visibleTags;
		if (visibleTags.length === 0) {
			return this.viewVisibleCollections();
		}

		if (this.state.zoom > ZOOM_SWITCH_JOBS_LEVEL && visibleTags.length < 5) {
			return this.viewVisibleCollections();
		}

		if (this.state.zoom > ZOOM_SWITCH_LEVEL) {
			return this.viewVisibleCollections();
		}

		return this.viewVisibleTags();
	}

	// In-progress line-draw preview; reads app-global LineDraw so tag & topo maps share one draw.
	private get lineDrawNodes(): MithrilVNode[] {
		return buildLineDrawNodes(
			CurrentState.LineDraw.state,
			this.pointDragger,
			(loc) => PointCache.getPoint(loc.lat(), loc.lng()),
			'tag-line-draw'
		);
	}

	private viewVisibleCollections(): MithrilVNode[] {
		const output: MithrilVNode[] = [];

		const nodesMap: { [tagId: string]: OverlayLayers } = {};

		for (const revision of this.state.visibleCollections) {
			const summary = AppStore.CollectionSummary.get(revision);
			if (summary == null) {
				continue;
			}

			// No location is not a reason to skip the collection — its drawn lines carry their own
			// coordinates and still render. Only the dot needs a position (@see CollectionMapPoint.view).
			const location = summary.location;

			const [tagId] = summary.tags;
			let existing = nodesMap[tagId];
			if (existing == null) {
				existing = nodesMap[tagId] = {
					lines: m('div', { key: 'layer-line-' + tagId, className: CSS.line }, []),
					points: m('div', { key: 'layer-point-' + tagId, 'c-id': tagId, className: CSS.point }, []),
				};
				output.push(existing.lines);
			}

			const point = location != null ? PointCache.getGeoPoint(location) : null;
			const points = CollectionMapPoint.view(this.state, summary, point);

			if (points.collection) {
				existing.points.children.push(points.collection);
			}

			if (points.line) {
				const child = existing.lines.children;
				for (const line of points.line) {
					child.push(line);
				}
			}
		}

		for (const key of Object.keys(nodesMap)) {
			const layers = nodesMap[key];
			output.push(layers.points);
		}

		return output;
	}

	private viewVisibleTags(): MithrilVNode[] {
		const dedupe: { [posKey: string]: TagJSONObject } = {};

		const nodes: MithrilVNode[] = [];
		for (const tagId of this.state.visibleTags) {
			const tag = AppStore.Tag.get(tagId);
			nodes.push(this.viewTag(tag, dedupe));
		}

		// Place into render order (first child is rendered on bottom)
		return nodes.reverse();
	}

	private viewTag(tag: TagJSONObject, dedupe: { [posKey: string]: TagJSONObject }): MithrilVNode {
		const summary = AppStore.TagSummary.get(tag.objectId);
		if (summary == null || summary.center == null) {
			return null;
		}

		const point = PointCache.getPoint(summary.center.lat, summary.center.lng);
		const cacheKey = `${Math.floor(point.x)}:${Math.floor(point.y)}`;

		if (dedupe[cacheKey] && CurrentState.hoverId !== tag.objectId) {
			return null;
		}

		dedupe[cacheKey] = tag;
		return TagMapPoint.view(tag, summary, point);
	}

	private verifyProjection(): boolean {
		const projection = this.overlay.getProjection();
		if (projection == null) {
			PointCache.reset();
			return true;
		}

		PointCache.setProjection(projection);

		const point = projection.fromLatLngToDivPixel(this.origin);
		const oldPoint = PointCache.getPoint(this.origin.lat(), this.origin.lng());
		if (point.x !== oldPoint.x || point.y !== oldPoint.y) {
			PointCache.reset();
			return true;
		}

		return false;
	}

	private updateMapBounds() {
		// Take bounds from URL
		if (this.state.bounds != null && this.state.zoom != null) {
			this.map.setCenter(this.state.bounds.getCenter());
			this.map.setZoom(this.state.zoom);
			return;
		}

		// Update bounds with collections when loaded
		this.centerReactionDisposer = reaction(
			() => this.state.tagState.fetchState,
			(fetchState) => {
				if (fetchState !== TagFetchState.UNKNOWN) {
					this.state.centerMap();
				}
			},
			{ fireImmediately: true, delay: 100 }
		);
	}
}
